import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { CONSTANTS } from 'clink-components';
import DocumentsListing from './DocumentsListing';
import i18next from 'i18next';
import { getAccountLogo } from 'v2/helpers/user';
import { useDispatch, useSelector } from 'react-redux';
import { Avatar, CircularProgress } from '@mui/material';
import RescindedPage from './RescindedPage';
import InvalidPage from './InvalidPage';
import AccessStateWrapper from './AccessStateWrapper';
import { PHPAppClinkGloblals } from 'v2/helpers/php-globals';
import { useParams, useNavigate } from 'react-router';
import { getDocumentSnapshot } from 'v2/store/reducers/clink/download-manager/asyncThunk';
import Cookies from 'js-cookie';
import { useSnackbar } from 'v2/hooks/useSnackbar';
import CustomDocQueueUI from './CustomDocQueueUI';
import DocumentsListingPreview from './DocumentsListingPreview';
import TenderRecommendationDocumentsListing from './TenderRecDocumentsListing';
import { httpHelperV2 } from 'v2/services/httpHelper';
import { handleUnauthorized } from 'v2/helpers/session';
import { useSearchParams } from 'react-router-dom';

const { white, clinkLightPurple } = CONSTANTS.colors.general;

const transformDocumentsData = (data) => {
  if (!data) return [];

  const projects = [];
  const {
    project_documents,
    tender_package_documents,
    additional_documents,
    state,
  } = data;

  if (project_documents && Object.keys(project_documents).length > 0) {
    const folders = Object.values(project_documents).map((folder) => ({
      id: folder?.id,
      name: folder?.label,
      files: (folder?.documents || []).map((doc) => ({
        id: doc?.id,
        name: doc?.name,
        download_uri: doc?.download_uri,
        download_id: doc?.download_id,
      })),
    }));

    projects.push({
      name: i18next.t('project-specific-documents'),
      folders,
    });
  }

  if (
    tender_package_documents &&
    tender_package_documents.documents?.length > 0
  ) {
    projects.push({
      name: i18next.t('tender-package-documents'),
      folders: [
        {
          id: tender_package_documents?.id,
          name: tender_package_documents?.label,
          files: tender_package_documents?.documents.map((doc) => ({
            id: doc?.id,
            name: doc?.name,
            download_uri: doc?.download_uri,
            download_id: doc?.download_id,
          })),
        },
      ],
    });
  }

  if (additional_documents && Object.keys(additional_documents).length > 0) {
    const folders = Object.values(additional_documents)
      .filter((folder) => folder.documents?.length > 0)
      .map((folder) => ({
        id: folder?.id,
        name: folder?.label,
        files: folder?.documents.map((doc) => ({
          id: doc?.id,
          name: doc?.name,
          download_uri: doc?.download_uri,
          download_id: doc?.download_id,
        })),
      }));

    if (folders?.length > 0) {
      projects.push({
        name: i18next.t('additional-documents'),
        folders,
      });
    }
  }

  return { projects, state };
};

const downloadZipFile = async (blob, filename) => {
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(downloadUrl);
};

const DownloadManager = () => {
  const config = PHPAppClinkGloblals();
  const { user } = useSelector((state) => state?.clinkAccount);
  const [isLoading, setIsLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [message, setMessage] = useState(null);
  const [state, setState] = useState(null);

  const [avatar, setAvatar] = useState(null);
  const { showSnackbar } = useSnackbar();
  // --- NEW: Loading states for downloads ---
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [isDownloadingAllTrDocs, setIsDownloadingAllTrDocs] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState(null); // tracks which single file is downloading
  const [downloadErrors, setDownloadErrors] = useState({}); // tracks download errors for each file
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('pid');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { documentId, context } = useParams();


  const getDocuments = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await dispatch(getDocumentSnapshot({ document_id: documentId, token: config?.token })).unwrap();
      const { projects, state: documentState } = transformDocumentsData(response);
      setDocuments(projects);
      setState(documentState);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setMessage(error?.message || i18next.t('failed-to-download-documents'));
    }
  }, [dispatch, documentId, config?.token]);

  // API call for retrieving the account logo
  useEffect(() => {
    const accountLogo = getAccountLogo(user?.account_id);
    setAvatar(accountLogo);
  }, [user?.account_id]);

  // API call for retrieving the document listing if config status is valid
  useEffect(() => {
    if (
      config?.status === 'valid' &&
      documentId &&
      context !== 'tender_recommendation_attachment'
    ) {
      getDocuments();
    }
  }, [config?.status, documentId, getDocuments]);



  const handleDownloadAll = async () => {
    if (isDownloadingAll) return;
    const token = Cookies.get(API.TOKEN_NAME);

    try {
      setIsDownloadingAll(true);
      const response = await fetch(
        `${API.RELAY_URL}document/${documentId}/snapshot/download-all`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${config?.token ? config?.token : token}`,
          },
        },
      );

      if (response.status === 401) { handleUnauthorized(); return; }
      if (!response.ok) throw new Error('Download failed');

      // Now headers are accessible
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename =
        contentDisposition?.split('filename=')?.[1]?.trim() ||
        `${documentId}.zip`;

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      showSnackbar(error?.message || i18next.t('failed-to-download-documents'), 'error');
    } finally {
      setIsDownloadingAll(false);
    }
  };

  const isSnapshotPreview = (projectName, accessState) =>
    projectName !== 'ADDITIONAL DOCUMENTS' && accessState === 'snapshot_preview';

  const getProjectCategory = (projectName) =>
    projectName === 'PROJECT SPECIFIC DOCUMENTS' ? 'project_documents' : 'tender_package_documents';

  const getDownloadFileId = (file, projectName, accessState) =>
    isSnapshotPreview(projectName, accessState) ? file?.download_id : file?.id;

  const handleDownload = async (file, folderLabel, projectName, accessState) => {
    const fileId = getDownloadFileId(file, projectName, accessState);
    if (downloadingFileId === fileId) return;

    try {
      setDownloadingFileId(fileId);
      setDownloadErrors((prev) => ({ ...prev, [fileId]: null }));
      let url = '';
      if (isSnapshotPreview(projectName, accessState)) {
        url = `${API.RELAY_URL}document/${documentId}/preview-mode/download?category=${getProjectCategory(projectName)}&folder=${folderLabel}&file=${file?.download_id}`;
      } else {
        url = `${API.RELAY_URL}document/download/${file?.id}`;
      }
      const token = Cookies.get(API.TOKEN_NAME);
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${config?.token ? config?.token : token}`,
        },
      });

      if (response.status === 401) { handleUnauthorized(); return; }
      if (!response.ok) {
        throw new Error('Download failed');
      }

      const downloadStatus = response.headers.get('X-Download-Status');

      if (downloadStatus === 'downloaded') {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = downloadUrl;
        link.download =
          file?.name || `document-${file?.id || file?.download_id}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        setDownloadErrors((prev) => ({ ...prev, [fileId]: true }));
        const displayName = folderLabel ? `${folderLabel} - ${file?.name}` : file?.name;
        showSnackbar(i18next.t('unable-to-download-toast-msg', { filename: displayName }), 'error');
      }
    } catch (error) {
      setDownloadErrors((prev) => ({ ...prev, [fileId]: true }));
      const displayName = folderLabel ? `${folderLabel} - ${file?.name}` : file?.name;
      showSnackbar(i18next.t('unable-to-download-toast-msg', { filename: displayName }), 'error');
    } finally {
      setDownloadingFileId(null);
    }
  };

  const getPageHeading = () => {
    switch (context) {
      case 'order':
        return i18next.t('document-downloads-title-order');
      case 'enquiry':
        return i18next.t('document-downloads-title-enquiry');
      case 'addendum':
        return i18next.t('document-downloads-title-addendum');
      case 'tender_recommendation_attachment':
        return i18next.t('document-downloads-title-tr');
      default:
        return i18next.t('document-downloads-title');
    }
  };

  const getPageDescription = () => {
    switch (context) {
      case 'order':
        return i18next.t('document-downloads-description-order');
      case 'enquiry':
        return i18next.t('document-downloads-description-enquiry');
      case 'addendum':
        return i18next.t('document-downloads-description-addendum');
      case 'tender_recommendation_attachment':
        return i18next.t('document-downloads-description-tr');
      default:
        return i18next.t('document-downloads-description');
    }
  };

  const renderDocumentsContent = () => {
    if (isLoading) {
      return <CustomDocQueueUI />;
    }
    if ((state === 'ready' || state === 'ready_partial') && documents?.length > 0) {
      return (
        <DocumentsListing
          projects={documents}
          onDownload={handleDownload}
          downloadingFileId={downloadingFileId}
          downloadErrors={downloadErrors}
        />
      );
    }
    if (state === 'snapshot_preview' && documents?.length > 0) {
      return (
        <DocumentsListingPreview
          projects={documents}
          onDownload={handleDownload}
          downloadingFileId={downloadingFileId}
          downloadErrors={downloadErrors}
          state={state}
        />
      );
    }
    return (
      <Typography data-testid="download-manager-empty-message" variant="body1" color="text.primary" textAlign="center" mt={10}>
        {message || i18next.t('no-documents-found')}
      </Typography>
    );
  };

  const renderMainContent = () => (
    <Container data-testid="download-manager-page" maxWidth="lg" className="download-manager-container">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2 }}>
        <Box>
          <Typography data-testid="download-manager-page-title" variant="h5" fontWeight="bold" color="text.primary" mb={0.5}>
            {getPageHeading()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getPageDescription()}
          </Typography>
        </Box>
        {(state === 'ready' || state === 'ready_partial') && (
          <Button
            data-testid="download-manager-download-all-btn"
            variant="outlined"
            color="inherit"
            startIcon={
              isDownloadingAll
                ? <CircularProgress size={16} color="inherit" />
                : <FileDownloadOutlinedIcon />
            }
            onClick={handleDownloadAll}
            disabled={isDownloadingAll}
          >
            {isDownloadingAll ? i18next.t('downloading') : i18next.t('download-all-documents')}
          </Button>
        )}
      </Box>
      {renderDocumentsContent()}
    </Container>
  );

  const renderPageBody = () => {
    if (config?.status === 'invalid') {
      return (
        <AccessStateWrapper
          component={InvalidPage}
          ownerAccountName={config?.ownerAccountName}
        />
      );
    }
    if (config?.status === 'rescinded') {
      return (
        <AccessStateWrapper
          component={RescindedPage}
          ownerAccountName={config?.ownerAccountName}
        />
      );
    }
    return renderMainContent();
  };

  return (
    <>
      <Box
        sx={{
          background: white,
          border: `1px solid ${clinkLightPurple}`,
          p: 2,
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Avatar
          alt="Company Logo"
          src={avatar || ''}
          className="company-logo-ddm"
          sx={{
            width: '150px',
            borderRadius: 0,
          }}
        />
        {context === 'tender_recommendation_attachment' && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,

              cursor: 'pointer',
              '&:hover': {
                opacity: 0.7,
              },
            }}
            onClick={() => navigate(-1)}
          >
            <ArrowBackIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {i18next.t('back-to-tender-recommendation')}
            </Typography>
          </Box>
        )}
      </Box>
      {renderPageBody()}
    </>
  );
};

export default DownloadManager;
